import React, { useState, useEffect } from 'react';
import axios from 'axios';
import md5 from "md5"; // Install md5 using: npm install md5
import { BaseUrl } from '../App';
import { Eye, EyeOff } from 'lucide-react';
import { Circles } from 'react-loader-spinner';
import { FadeLoader } from 'react-spinners';


const AdminProfile = () => {
  // State for managing the change password section visibility
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [error, setError] = useState("");
  
  // State for password visibility
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  // State for validation errors
  const [validationErrors, setValidationErrors] = useState({});
  
  // State for password fields
  const [passwordData, setPasswordData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  // State for managing form inputs
  const [formData, setFormData] = useState({
    fullname: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    country: '',
    password: '',
  });

  // State for original fullname
  const [originalFullname, setOriginalFullname] = useState('');

  // State for the userId (assuming you pass this as a prop or from a global state)
  const userId = 1; // Example static value, replace with actual user ID
  useEffect(() => {
    let timer;
    if (error) {
      timer = setTimeout(() => {
        setError("");
      }, 5000); // 5 seconds
    }
    return () => clearTimeout(timer);
  }, [error]);
  // Function to fetch the existing profile data when the component is mounted
  useEffect(() => {
    setLoading(true);
    axios.post(`${BaseUrl}/backend/api/profile_data.php`, {
      action: 'get_profile_detail',
      user_id: userId,
      type: 'admin', // Or 'user', based on your user type
      api_key: '1', // Replace with your actual API key
    })
    .then(response => {
      if (response.data.status === 'true') {
        const userData = response.data.response[0]; // Access the first object in the array
        setFormData({
          fullname: userData.fullname || '',
          email: userData.user_email || '',
          phone: userData.phone || '',
          address: userData.address || '',
          city: userData.city || '',
          state: userData.state || '',
          zip: userData.zip || '',
          country: userData.country || '',
          password: userData.password || '', // Clear password field
        });
        setOriginalFullname(userData.fullname || '');
        setPasswordData({
          ...passwordData,
          oldPassword: userData.password || ''
        });
      } else {
        console.error('Failed to fetch profile data:', response.data.response);
      }
    })
    .catch(error => {
      console.error('Error fetching profile data:', error);
    })
    .finally(() => {
      setLoading(false); // Ensure loading state is updated
    });
  
  }, [userId]);
  
  
  // Function to validate the form
  const validateForm = () => {
    const errors = {};
    
    // Email validation
    if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = "Please enter a valid email address";
    }
    
    // Phone validation - must be 10 digits
    if (!/^\d{10}$/.test(formData.phone)) {
      errors.phone = "Phone number must be 10 digits";
    }
    
    // Zip code validation - must be 6 digits
    if (formData.zip && !/^\d{6}$/.test(formData.zip)) {
      errors.zip = "Zip code must be 6 digits";
    }
    
    // Full name cannot be changed
    if (formData.fullname !== originalFullname) {
      errors.fullname = "Full name cannot be changed";
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  // Function to validate passwords
  const validatePasswords = () => {
    const errors = {};
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      errors.confirmPassword = "New password and confirm password must match";
    }
    
    if (passwordData.newPassword && passwordData.newPassword.length < 6) {
      errors.newPassword = "Password must be at least 6 characters";
    }
    
    setValidationErrors({...validationErrors, ...errors});
    return Object.keys(errors).length === 0;
  };

  // Function to handle form field changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Clear validation error when user starts typing
    if (validationErrors[name]) {
      setValidationErrors({
        ...validationErrors,
        [name]: null
      });
    }
    
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };
  
  // Function to handle password field changes
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    
    // Clear validation error when user starts typing
    if (validationErrors[name]) {
      setValidationErrors({
        ...validationErrors,
        [name]: null
      });
    }
    
    setPasswordData({
      ...passwordData,
      [name]: value
    });
  };

  // Function to handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(false);
    // Reset messages
    setSuccessMessage("");
    setError("");
    
    // Set fullname back to original if it was changed
    setFormData({
      ...formData,
      fullname: originalFullname
    });
  
    // Validate the form
    if (!validateForm()) {
      setError("Please fix the errors before submitting.");
      return;
    }
  
    // Validate passwords if the password section is shown
    if (showChangePassword) {
      if (!validatePasswords()) {
        setError("Please fix the password errors before submitting.");
        return;
      }
    }
  
    // Create the request payload
    const payload = {
      action: "profile_detail_update",
      user_id: userId,
      type: "admin", // Or 'staff' if updating staff profile
      api_key: "1", // Replace with actual API key
      fullname: originalFullname, // Always use original fullname
      email: formData.email,
      phone: formData.phone,
      address: formData.address,
      city: formData.city,
      state: formData.state,
      zip: formData.zip,
      country: formData.country,
    };
  
    // ✅ Add password in MD5 format if changing password
    // if (showChangePassword && passwordData.newPassword) {
    //   payload.pass = md5(passwordData.newPassword);
    // }
  
    axios.post(`${BaseUrl}/backend/api/profile_data_update.php`, payload)
      .then(response => {
        if (response.data.status === "true") {
          setSuccessMessage("Admin Profile Updated Successfully!");
  
          // ✅ Reset password fields if successful
          // if (showChangePassword) {
          //   setPasswordData({
          //     oldPassword: passwordData.newPassword,
          //     newPassword: "",
          //     confirmPassword: ""
          //   });
          //   setShowChangePassword(false);
          // }
        } else {
          setError(response.data.response || "Something went wrong, please try again.");
        }
      })
      .catch(error => {
        console.error('Error fetching profile data:', error);
      })
      .finally(() => {
        setLoading(false); // Ensure loading state is updated
      });
      
  };

  return (
    <div className="relative min-h-screen">
    {/* Loading Indicator */}
      {loading && (
           <div className="fixed inset-0 flex items-center justify-center z-50">
             <div className="flex flex-col items-center p-3 bg-black border border-white rounded-2xl">
               <FadeLoader 
                 height={15}
                 width={5}
                 color="#ffffff"
                 ariaLabel="loading"
               />
               <h1 className="mt-1 text-lg text-white">Please wait...</h1>
             </div>
           </div>
         )}
         
    <div className="p-4 sm:p-6 lg:p-8 bg-gray-50 ">
      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-md p-6">
        {successMessage && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-6 flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <span className="block sm:inline ml-2">{successMessage}</span>
          </div>
        )}

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-6 flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <span className="block sm:inline ml-2">{error}</span>
          </div>
        )}

        <div className="mb-8 w-full">
          <h1 className="text-1xl font-bold text-gray-800 mb-2 w-full">Admin Profile</h1>
          <p className="text-gray-600">Manage your personal information and account settings</p>
          {/* <div className="h-1 w-20 bg-blue-600 mt-2"></div> */}
        </div>

        {/* Personal Information Section */}
        <div className="space-y-6 mb-8">
          <h2 className="text-2xl font-semibold text-gray-800 border-b pb-2">Personal Information</h2>
          
          {/* Full Name */}
          <div>
            <label className="block text-lg font-medium text-gray-700 mb-1">Full Name</label>
            <input
              type="text"
              name="fullname"
              value={formData.fullname}
              onChange={handleInputChange}
              placeholder="Enter your full name"
              className={`p-3 border ${validationErrors.fullname ? 'border-red-500' : 'border-gray-300'} rounded-md w-full bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500`}
              readOnly
            />
            {validationErrors.fullname && (
              <p className="mt-1 text-red-500 text-sm">{validationErrors.fullname}</p>
            )}
            <p className="mt-1 text-sm text-red-500 ">Full name cannot be changed</p>
          </div>

          {/* Email */}
          <div>
            <label className="block text-lg font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="Enter your email"
              className={`p-3 border ${validationErrors.email ? 'border-red-500' : 'border-gray-300'} rounded-md w-full focus:outline-none focus:ring-2 focus:ring-blue-500`}
            />
            {validationErrors.email && (
              <p className="mt-1 text-red-500 text-sm">{validationErrors.email}</p>
            )}
          </div>

          {/* Phone Number */}
          <div>
            <label className="block text-lg font-medium text-gray-700 mb-1">Phone Number</label>
            <input
              type="text"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              placeholder="Enter your phone number"
              className={`p-3 border ${validationErrors.phone ? 'border-red-500' : 'border-gray-300'} rounded-md w-full focus:outline-none focus:ring-2 focus:ring-blue-500`}
            />
            {validationErrors.phone && (
              <p className="mt-1 text-red-500 text-sm">{validationErrors.phone}</p>
            )}
            {/* <p className="mt-1 text-sm text-gray-500">Phone number must be 10 digits</p> */}
          </div>

          {/* Address */}
          <div>
            <label className="block text-lg font-medium text-gray-700 mb-1">Address</label>
            <textarea
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              placeholder="Enter your address"
              className="p-3 border border-gray-300 rounded-md w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows="3"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* City */}
            <div>
              <label className="block text-lg font-medium text-gray-700 mb-1">City</label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                placeholder="Enter your city"
                className="p-3 border border-gray-300 rounded-md w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* State */}
            <div>
              <label className="block text-lg font-medium text-gray-700 mb-1">State</label>
              <input
                type="text"
                name="state"
                value={formData.state}
                onChange={handleInputChange}
                placeholder="Enter your state"
                className="p-3 border border-gray-300 rounded-md w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Zip Code */}
            <div>
              <label className="block text-lg font-medium text-gray-700 mb-1">Zip Code</label>
              <input
                type="text"
                name="zip"
                value={formData.zip}
                onChange={handleInputChange}
                placeholder="Enter your zip code"
                className={`p-3 border ${validationErrors.zip ? 'border-red-500' : 'border-gray-300'} rounded-md w-full focus:outline-none focus:ring-2 focus:ring-blue-500`}
              />
              {validationErrors.zip && (
                <p className="mt-1 text-red-500 text-sm">{validationErrors.zip}</p>
              )}
              {/* <p className="mt-1 text-sm text-gray-500">Zip code must be 6 digits</p> */}
            </div>

            {/* Country */}
            <div>
              <label className="block text-lg font-medium text-gray-700 mb-1">Country</label>
              <input
                type="text"
                name="country"
                value={formData.country}
                onChange={handleInputChange}
                placeholder="Enter your country"
                className="p-3 border border-gray-300 rounded-md w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Change Password Section */}
        {/* <div className="mt-10">
          <button
            type="button"
            onClick={() => setShowChangePassword(!showChangePassword)}
            className="flex items-center py-2 px-4 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition duration-200 font-medium"
          >
            {showChangePassword ? 'Cancel Password Change' : 'Change Password'}
            <svg className="ml-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              {showChangePassword ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7"></path>
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
              )}
            </svg>
          </button> */}
{/* 
          {showChangePassword && (
            <div className="mt-6 space-y-4 bg-gray-50 p-6 rounded-md border border-gray-200">
              <h3 className="text-xl font-semibold text-gray-800 mb-4">Change Your Password</h3>
              
             
              <div>
                <label className="block text-lg font-medium text-gray-700 mb-1">Old Password</label>
                <div className="relative">
                  <input
                    type={showOldPassword ? "text" : "password"}
                    name="oldPassword"
                    value={passwordData.oldPassword}
                    onChange={handlePasswordChange}
                    placeholder="Enter your old password"
                    className="p-3 border border-gray-300 rounded-md w-full pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    readOnly
                    disabled
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-600 cursor-pointer"
                    onClick={() => setShowOldPassword(!showOldPassword)}
                  >
                    {showOldPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              
              <div>
                <label className="block text-lg font-medium text-gray-700 mb-1">New Password</label>
                <div className="relative">
                  <input
                    type={showNewPassword ? "text" : "password"}
                    name="newPassword"
                    value={passwordData.newPassword}
                    onChange={handlePasswordChange}
                    placeholder="Enter your new password"
                    className={`p-3 border ${validationErrors.newPassword ? 'border-red-500' : 'border-gray-300'} rounded-md w-full pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-600 cursor-pointer"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                  >
                    {showNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                {validationErrors.newPassword && (
                  <p className="mt-1 text-red-500 text-sm">{validationErrors.newPassword}</p>
                )}
              </div>

             
              <div>
                <label className="block text-lg font-medium text-gray-700 mb-1">Confirm New Password</label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    name="confirmPassword"
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordChange}
                    placeholder="Confirm your new password"
                    className={`p-3 border ${validationErrors.confirmPassword ? 'border-red-500' : 'border-gray-300'} rounded-md w-full pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-600 cursor-pointer"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                {validationErrors.confirmPassword && (
                  <p className="mt-1 text-red-500 text-sm">{validationErrors.confirmPassword}</p>
                )}
              </div>
              
              
            </div>
          )}
        </div> */}

        {/* Submit Button */}
        <div className="mt-8 flex justify-end">
          <button
            type="submit"
            onClick={handleSubmit}
            className="py-3 px-8 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 font-medium shadow-md"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
       
  </div>
  );
}

export default AdminProfile;



